<script setup>
import axios from 'axios';
import Panzoom from '@panzoom/panzoom';
import { Capacitor } from '@capacitor/core';
import { Printer } from '@capgo/capacitor-printer';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route = useRoute();

const props = defineProps({
    title: {
        type: String
        , required: true
    }
    , apiUrl: {
        type: String
        , required: true
    },
    params: {
        type: Object,
        default: () => ({})
    },
    showHeader: {
        type: Boolean,
        default: true
    },
    printable: {
        type: Boolean,
        default: true
    }
});

// 複数ページのPDFを表示する場合の間隔（px）
const PAGE_GAP_PX = 10;

// canvas に描画するときの解像度倍率
const RENDER_SCALE = 3;

/**
 * タッチ主体の端末（スマホ・タブレット）では overflow 時に最初から Panzoom モードとするかどうかを判定する
 * @returns {boolean} Panzoom 常時モードとするかどうか（true: する, false: しない）
 */
const detectUsesDeferredOverflowZoomUi = () => {
    if (Capacitor.isNativePlatform()) {
        return true;
    }

    if (typeof window === 'undefined') {
        return false;
    }

    // タッチ主体の端末（スマホ・タブレット）では overflow 時に最初から Panzoom モードとする
    return window.matchMedia('(pointer: coarse) and (hover: none)').matches;
};

let usesDeferredOverflowZoomUi = detectUsesDeferredOverflowZoomUi();

const loading = ref(true);
const error = ref('');
const pdfBase64 = ref('');
const pdfContent = ref(null);
const pdfContainer = ref(null);
const pdfViewport = ref(null);
const pdfZoomLayer = ref(null);
const pageAspectRatios = ref([]);
const isPdfOverflowing = ref(false);

let panzoomInstance = null;
let wheelHandler = null;
let wheelTargetElement = null;
let resizeObserver = null;
let resizeRaf = null;

/**
 * 閉じるボタンをクリックしたときの処理
 */
const close = async() => {

    const isWeb = Capacitor.getPlatform() === 'web';
    if (isWeb) {
        window.close();
        return;
    }

    const reopenModal = route.query.reopenModal;
    const returnPath = route.query.returnPath;

    if (reopenModal && returnPath) {
        await router.push({
            path: returnPath
            , query: { reopenModal }
        });
    } else if(returnPath) {
        // 同意しなくてもいいPDFはモーダルなしで戻る
        await router.push({
            path: returnPath
        });
    } else if (window.history.length > 1) {
        await router.back();
    } else {
        await router.push('/');
    }
};

/**
 * 印刷ジョブ名を生成する
 * @returns {string} 印刷ジョブ名
 */
const getPrintJobName = () => props.title.trim() || 'document';

/**
 * ArrayBuffer を Base64 文字列に変換する
 * @param {ArrayBuffer} buffer - 変換対象の ArrayBuffer
 * @returns {string} Base64 文字列
 */
const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';

    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }

    return btoa(binary);
};

/**
 * 印刷ボタンをクリックしたときの処理
 * 取得済みの PDF データを印刷する（画面の canvas ではなく元の PDF）
 */
const printPdf = async() => {
    if (!pdfBase64.value) {
        return;
    }

    await Printer.printBase64({
        name: getPrintJobName(),
        data: pdfBase64.value,
        mimeType: 'application/pdf',
    });
};

/**
 * ページを canvas に描画する
 * @param {Object} page - pdf.js のページオブジェクト
 */
const renderPageToCanvas = async(page) => {
    const viewport = page.getViewport({ scale: RENDER_SCALE });
    const aspectRatio = viewport.height / viewport.width;
    pageAspectRatios.value.push(aspectRatio);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = '100%';
    canvas.style.display = 'block';
    canvas.style.border = '1px solid #ddd';
    canvas.style.boxSizing = 'border-box';

    pdfZoomLayer.value.appendChild(canvas);
    await page.render({ canvasContext: ctx, viewport }).promise;
};

/**
 * ズームレイヤーのスタイルをクリアする
 */
const clearZoomLayerStyles = () => {
    if (!pdfZoomLayer.value) {
        return;
    }

    pdfZoomLayer.value.style.transform = '';
};

/**
 * 文字列をピクセル値に変換する
 * @param {string} value - 文字列
 * @returns {number} ピクセル値
 */
const parsePx = (value) => Number.parseFloat(value) || 0;

/**
 * 利用可能なPDFの幅を計算する
 * @returns {number} 利用可能なPDFの幅
 */
const getAvailablePdfWidth = () => {
    if (!pdfViewport.value || !pdfContent.value) {
        return 0;
    }

    const viewportStyle = window.getComputedStyle(pdfViewport.value);
    const paddingX = parsePx(viewportStyle.paddingLeft) + parsePx(viewportStyle.paddingRight);
    return Math.max(pdfContent.value.clientWidth - paddingX, 0);
};

/**
 * ページの高さを計算する
 * @param {number} availableWidth - 利用可能な幅
 * @returns {number} ページの高さ
 */
const getTotalPageHeight = (availableWidth) => {
    const totalPageHeight = pageAspectRatios.value.reduce(
        (sum, ratio) => sum + (availableWidth * ratio),
        0,
    );
    const totalGap = PAGE_GAP_PX * Math.max(pageAspectRatios.value.length - 1, 0);
    return totalPageHeight + totalGap;
};

/**
 * 利用可能なコンテンツの高さを計算する
 * @returns {number} 利用可能なコンテンツの高さ
 */
const getAvailableContentHeight = () => {
    if (pdfContent.value?.clientHeight > 0) {
        return pdfContent.value.clientHeight;
    }

    if (!pdfContainer.value) {
        return window.innerHeight;
    }

    const headerEl = pdfContainer.value.querySelector('.pdf-header');
    const headerHeight = headerEl?.offsetHeight ?? 0;
    const parentHeight = pdfContainer.value.parentElement?.clientHeight ?? window.innerHeight;
    return Math.max(parentHeight - headerHeight, 0);
};

/**
 * ビューポートの状態をキャプチャする
 * @returns {Object} ビューポートの状態
 */
const captureViewState = () => ({
    scale: panzoomInstance?.getScale() ?? 1,
    pan: panzoomInstance?.getPan() ?? { x: 0, y: 0 },
});

/**
 * ビューポートの状態を復元する
 * @param {Object} state - ビューポートの状態
 */
const restoreViewState = (state) => {
    if (!panzoomInstance) {
        return;
    }

    if (Math.abs(panzoomInstance.getScale() - state.scale) > 0.001) {
        panzoomInstance.zoom(state.scale, { animate: false });
    }

    panzoomInstance.pan(state.pan.x, state.pan.y, {
        animate: false,
        force: true,
    });
};

/**
 * ビューポートのサイズをPDFのサイズに同期する
 */
const syncViewportSizeToPdf = () => {
    if (!pdfViewport.value || !pdfContent.value || pageAspectRatios.value.length === 0) {
        return;
    }

    const viewportStyle = window.getComputedStyle(pdfViewport.value);
    const paddingY = parsePx(viewportStyle.paddingTop) + parsePx(viewportStyle.paddingBottom);
    const availableWidth = getAvailablePdfWidth();
    if (!availableWidth) {
        return;
    }

    const totalPdfHeight = getTotalPageHeight(availableWidth) + paddingY;
    const availableContentHeight = getAvailableContentHeight();
    const overflows = totalPdfHeight > availableContentHeight + 1;

    isPdfOverflowing.value = overflows;

    pdfContent.value.style.height = '';
    pdfViewport.value.style.minHeight = '';
    pdfViewport.value.style.height = '';

    if (overflows) {
        if (usesDeferredOverflowZoomUi) {
            // タッチ端末 + overflow: 親（pdfViewport）を見える窓サイズに固定し、子（pdfZoomLayer）は全文高さのまま Panzoom で縦パン
            pdfViewport.value.style.height = `${Math.max(availableContentHeight, 0)}px`;
            return;
        }

        if (pdfZoomLayer.value) {
            void pdfZoomLayer.value.offsetHeight;
        }
        pdfViewport.value.style.minHeight = `${Math.max(totalPdfHeight, 0)}px`;
        return;
    }

    pdfViewport.value.style.height = `${Math.max(totalPdfHeight, 0)}px`;
};

/**
 * PDFをビューポートの角に合わせる
 */
const alignPdfToViewportCorners = () => {
    if (!panzoomInstance || !pdfViewport.value || !pdfZoomLayer.value) {
        return;
    }

    const viewportRect = pdfViewport.value.getBoundingClientRect();
    const layerRect = pdfZoomLayer.value.getBoundingClientRect();
    const viewportStyle = window.getComputedStyle(pdfViewport.value);
    const targetLeft = viewportRect.left + parsePx(viewportStyle.paddingLeft);
    const targetTop = viewportRect.top + parsePx(viewportStyle.paddingTop);
    const deltaX = targetLeft - layerRect.left;
    const deltaY = targetTop - layerRect.top;

    if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) {
        return;
    }

    const { x, y } = panzoomInstance.getPan();
    const scale = panzoomInstance.getScale();
    panzoomInstance.pan(x + (deltaX / scale), y + (deltaY / scale), {
        animate: false,
        force: true,
    });
};

/**
 * パンズームを破棄する
 */
const destroyPanzoom = () => {
    if (wheelTargetElement && wheelHandler) {
        wheelTargetElement.removeEventListener('wheel', wheelHandler);
    }

    panzoomInstance?.destroy();
    panzoomInstance = null;
    wheelHandler = null;
    wheelTargetElement = null;
    clearZoomLayerStyles();
};

/**
 * パンズームを初期化する（タッチ端末のみ。PC ではスクロールのみ）
 */
const initPanzoom = () => {
    if (!usesDeferredOverflowZoomUi || !pdfZoomLayer.value || !pdfViewport.value || panzoomInstance) {
        return;
    }

    panzoomInstance = Panzoom(pdfZoomLayer.value, {
        maxScale: 4,
        minScale: 1,
        step: 0.5,
        canvas: true,
        contain: 'outside',
        touchAction: 'none',
        panOnlyWhenZoomed: !isPdfOverflowing.value,
    });

    if (!isPdfOverflowing.value) {
        wheelHandler = panzoomInstance.zoomWithWheel;
        wheelTargetElement = pdfViewport.value;
        wheelTargetElement.addEventListener('wheel', wheelHandler, { passive: false });
    }

    requestAnimationFrame(() => {
        alignPdfToViewportCorners();
    });
};

/**
 * レイアウトのリサイズを処理する
 */
const handleLayoutResize = () => {
    if (!pdfContent.value || !pdfViewport.value || loading.value) {
        return;
    }

    if (resizeRaf) {
        cancelAnimationFrame(resizeRaf);
    }

    resizeRaf = requestAnimationFrame(() => {
        resizeRaf = requestAnimationFrame(() => {
            resizeRaf = null;
            const prevOverflows = isPdfOverflowing.value;
            const viewState = captureViewState();
            syncViewportSizeToPdf();

            const overflowModeChanged = prevOverflows !== isPdfOverflowing.value;
            const mobileOverflowLayoutChanged = usesDeferredOverflowZoomUi && isPdfOverflowing.value;

            if (panzoomInstance && (overflowModeChanged || mobileOverflowLayoutChanged)) {
                destroyPanzoom();
                initPanzoom();
            }

            restoreViewState(viewState);
        });
    });
};

/**
 * リサイズオブザーバーを開始する
 */
const startResizeObserver = () => {
    if (!pdfContent.value || resizeObserver) {
        return;
    }

    resizeObserver = new ResizeObserver(handleLayoutResize);
    resizeObserver.observe(pdfContent.value);
};

/**
 * リサイズオブザーバーを停止する
 */
const stopResizeObserver = () => {
    if (resizeRaf) {
        cancelAnimationFrame(resizeRaf);
        resizeRaf = null;
    }

    resizeObserver?.disconnect();
    resizeObserver = null;
};

/**
 * マウント時の処理
 */
onMounted(async() => {
    document.title = `共想未来ポータル | ${props.title}`;
    usesDeferredOverflowZoomUi = detectUsesDeferredOverflowZoomUi();

    const pdfjsLib = window['pdfjsLib'];
    if (!pdfjsLib) {
        error.value = 'PDFライブラリの読み込みに失敗しました';
        loading.value = false;
        return;
    }

    pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

    try {
        const res = await axios.get(
            props.apiUrl
            , {
                responseType: 'arraybuffer'
                , params: props.params
            }
        );

        const pdfData = res.data;
        pdfBase64.value = arrayBufferToBase64(pdfData);

        const pdf = await pdfjsLib.getDocument({
            data: pdfData
        }).promise;

        await nextTick();

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            await renderPageToCanvas(page);
        }

        await nextTick();
        loading.value = false;
        await nextTick();
        syncViewportSizeToPdf();
        initPanzoom();
        startResizeObserver();
    } catch (e) {
        loading.value = false;
        error.value = 'PDFの読み込みに失敗しました';
    }
});

onUnmounted(() => {
    stopResizeObserver();
    destroyPanzoom();
});

</script>

<template>
    <div
        ref="pdfContainer"
        class="pdf-container"
    >
        <div v-if="showHeader" class="pdf-header">
            <span>{{ title }}</span>
            <div class="pdf-header-actions">
                <button
                    v-if="printable"
                    type="button"
                    class="header-btn"
                    :disabled="loading || !!error"
                    @click="printPdf"
                >
                    印刷
                </button>
                <button type="button" class="header-btn" @click="close">閉じる</button>
            </div>
        </div>

        <div
            ref="pdfContent"
            class="pdf-content"
            :class="{
                'pdf-content--scrollable': isPdfOverflowing && !usesDeferredOverflowZoomUi,
                'pdf-content--zoomed': isPdfOverflowing && usesDeferredOverflowZoomUi,
            }"
        >
            <div v-if="loading" class="loading">読み込み中...</div>
            <div v-if="error" class="error">{{ error }}</div>
            <div
                v-show="!loading && !error"
                ref="pdfViewport"
                class="pdf-viewport"
                :class="{
                    'pdf-viewport--scrollable': isPdfOverflowing && !usesDeferredOverflowZoomUi,
                    'pdf-viewport--panzoom': isPdfOverflowing && usesDeferredOverflowZoomUi,
                }"
            >
                <div ref="pdfZoomLayer" class="pdf-zoom-layer"></div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.pdf-container {
    display: flex;
    flex-direction: column;
    max-width: 1000px;
    min-width: min(100vw, 820px);
    margin-top: env(safe-area-inset-top);
    margin-left: auto;
    margin-right: auto;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    overflow: hidden;
}

.pdf-header {
    flex-shrink: 0;
    background: linear-gradient(56deg, #007bd1 0%, #0dabe2 50%, #19ccaa 100%);
    color: white;
    padding: 15px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.pdf-content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

.pdf-content--scrollable {
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
}

/* タッチ端末 + overflow: Panzoom 常時モード（縦パンで全文を閲覧） */
.pdf-content--zoomed {
    overflow: hidden;
    touch-action: none;
}

.pdf-viewport {
    padding: 0px;
    overflow: hidden;
    touch-action: none;
}

.pdf-viewport--scrollable {
    overflow: visible;
}

.pdf-viewport--panzoom {
    overflow: hidden;
    touch-action: none;
}

.pdf-zoom-layer {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    transform-origin: 0 0;
    touch-action: none;
}

.pdf-viewport--panzoom .pdf-zoom-layer {
    touch-action: none;
}

.loading, .error {
    text-align: center;
    padding: 50px;
}

.error {
    color: #dc3545;
}

.pdf-header-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
}

.header-btn {
    background: rgba(255,255,255,0.2);
    border: none;
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
}

.header-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

</style>
